import {
  PATIENT_FIELD_ERRORS,
  normalizePhoneDigits,
  toE164,
} from "@/app/components/studio/SchedulingWidget/scheduling.helpers";
import { SHARED_COPY, THREE_STEP_SERVICES, type ThreeStepServiceKey } from "./services";

/**
 * The scheduler's content seam (tag-delivery section 0, WP-3 of the 2026-09-07
 * scheduler audits).
 *
 * Every string the 3-step scheduler and the confirmation page render lives in
 * one typed object, SchedulerContent. Its DEFAULTS are the approved board
 * strings in ./services.ts (SHARED_COPY plus the per-service row), so a bare
 * render is byte-identical to the boards. Its OVERRIDES come from Contentful:
 * the four Studio presets (app/components/studio/ThreeStepScheduler/presets.tsx)
 * register one Text or Boolean variable per leaf of this object, and the
 * standalone /smile/schedule route reads the same variables off a placed
 * preset in the smile/bb/scheduler slot experience. Legal, marketing and brand
 * staff edit copy in Contentful; a release is never needed for a comma.
 *
 * Booking identity (reason, questionnaire token, registry slug) is NOT content
 * and never passes through here - services.ts and variants.ts own it.
 *
 * Studio ids: a variable id may be at most 32 characters (the Studio canvas
 * throws for the whole batch otherwise - see SmileButton/index.ts), so every
 * leaf path maps to a deterministic id through schedulerContentVariableId;
 * content.test.ts pins the mapping and the length.
 */

export interface SchedulerFaqContent {
  question: string;
  answer: string;
  footnote: string;
}

export interface SchedulerCarrierContent {
  /** Carrier name, used as the mark's alt text. */
  name: string;
  /** Brandfolder CDN URL of the mark; empty drops the tile (a name is never rendered as a text tile). */
  logoSrc: string;
}

export interface SchedulerContent {
  /** Page h1. May carry the boards' U+2028 line separator (see headlineLines). */
  headline: string;
  /** Display name for the {service} token (availability line, review heading, ICS summary). */
  serviceName: string;
  /** Header lede under the h1; empty = not rendered (hidden layer, D-01). */
  availabilityLine: string;
  /** "Need assistance? Call " prefix under the h1; empty = not rendered (D-01). */
  assistLinePrefix: string;
  /** "Need help booking? Call " prefix under the accordion; empty = not rendered. */
  helpLinePrefix: string;
  contactPhoneDisplay: string;
  contactPhoneNumber: string;
  retry: string;
  /** The "Required" tag in every field label row (step 1 and step 2). */
  requiredTag: string;
  /** Renders the SMS consent row on step 2 (default off, decision D-05). */
  showSmsConsent: boolean;
  /** Renders the parent/guardian fieldset for a minor DOB (default on: the
   * booking write carries the guardian, TAG-171). */
  showGuardianFields: boolean;
  steps: { locationTime: string; patientInformation: string; review: string };
  step1: {
    heading: string;
    subheading: string;
    zipLabel: string;
    zipPlaceholder: string;
    search: string;
    selectedLocation: string;
    nearest: string;
    changeLocation: string;
    /** Results heading for two or more offices; tokens {count}, {zip}. */
    officesNear: string;
    /** Results heading for exactly one office; tokens {count}, {zip}. */
    officesNearOne: string;
    showMore: string;
    chooseDate: string;
    chooseDateChip: string;
    /** Day-chip date line pattern; tokens {month} (AP abbreviation), {day}, {ordinal}. */
    dayChipDate: string;
    /** Notice over the strip when the first day with times is not today; token {date}. */
    nextAvailable: string;
    /** Accessible names of the strip's Earlier / Later arrows (960+). */
    earlierDays: string;
    laterDays: string;
    chooseTime: string;
    morning: string;
    afternoon: string;
    showAll: string;
    milesAway: string;
    continueCta: string;
    zipInvalidError: string;
    officeNotFoundError: string;
    officeSearchUnavailable: string;
    noOfficesTemplate: string;
    loadingTimes: string;
    timesLoadFailed: string;
    callOffice: string;
    noTimesThisDay: string;
    slotTakenError: string;
  };
  step2: {
    /** Intro pair over the fields; empty = not rendered (hidden layer on every board, D-01). */
    heading: string;
    subheading: string;
    /** "Let us get to know you." pair over the Yes/No groups; empty = not rendered (hidden layer, D-01). */
    knowHeading: string;
    knowSubheading: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    dobPlaceholder: string;
    dobHelper: string;
    phone: string;
    phonePlaceholder: string;
    phoneHelper: string;
    smsConsent: string;
    email: string;
    emailPlaceholder: string;
    insuranceQuestion: string;
    medicaidNote: string;
    yes: string;
    no: string;
    required: string;
    continueCta: string;
    /** Yes/No service question; empty = the fieldset is not rendered. */
    serviceQuestion: string;
  };
  guarantor: {
    heading: string;
    subheading: string;
    firstName: string;
    lastName: string;
    relationship: string;
    relationshipPlaceholder: string;
    phone: string;
  };
  step3: {
    heading: string;
    subheading: string;
    officeLocation: string;
    dateTime: string;
    patientInformation: string;
    date: string;
    time: string;
    /** "Date:" value grammar; tokens {weekday}, {month} (AP abbreviation), {day}, {ordinal}. */
    dateTemplate: string;
    edit: string;
    confirmCta: string;
    sending: string;
    ineligibleError: string;
    unavailableError: string;
  };
  fieldErrors: Record<keyof typeof PATIENT_FIELD_ERRORS, string>;
  confirmation: {
    heading: string;
    dateTime: string;
    location: string;
    addToCalendar: string;
    directions: string;
    call: string;
    calendarSummary: string;
    paperwork: string;
    fillOutForms: string;
    insuranceHeading: string;
    insuranceFootnoteMarker: string;
    insuranceSubheading: string;
    seeAllInsurance: string;
    noInsuranceHeading: string;
    noInsuranceBody: string;
    savingsPlanFootnoteMarker: string;
    enrollToday: string;
    meetYourTeam: string;
    meetSubheading: string;
    haveQuestions: string;
    disclaimerInsurance: string;
    disclaimerSavingsPlan: string;
    appointmentCardTitle: string;
    journeyHeading: string;
    journeyVideoTitle: string;
    journeyVideoBody: string;
    faqSubtitle: string;
    faqFootnoteMarker: string;
    faq: SchedulerFaqContent[];
    carriers: SchedulerCarrierContent[];
    // ── Success page imagery and destinations (WP4, 2026-09-08) ─────────────
    // Every image and video default is a Brandfolder CDN URL
    // (https://cdn.bfldr.com/LP3GHVAP/as/<id>/<name>); an empty value renders
    // nothing for that element or section - never a placeholder.
    /** Hero photograph above the h1 (1024:24251 / 24502). Empty = no hero. */
    heroImageSrc: string;
    heroImageAlt: string;
    /** Patient-forms destination of the paperwork band; empty hides the band. */
    fillOutFormsHref: string;
    seeAllInsuranceHref: string;
    enrollTodayHref: string;
    /** Poster of the journey video card (1024:24411 / 24658); empty with no video hides the section. */
    journeyPosterSrc: string;
    /** MP4 of the journey video; empty renders the poster alone. */
    journeyVideoSrc: string;
    /** Photograph in the Have Questions? header (1024:24451 / 24706). Empty = no image. */
    faqImageSrc: string;
    faqImageAlt: string;
  };
}

type DeepPartial<T> = T extends readonly (infer Item)[]
  ? (DeepPartial<Item> | undefined)[]
  : T extends object
    ? { [Key in keyof T]?: DeepPartial<T[Key]> }
    : T;

/** Authored values: any subset of the content tree, arrays by slot. */
export type SchedulerContentOverrides = DeepPartial<SchedulerContent>;

export type SchedulerContentLeaf = string | boolean;

/** FAQ and carrier slots registered on every preset (no Array variables in Studio). */
export const SCHEDULER_FAQ_SLOTS = 4;
export const SCHEDULER_CARRIER_SLOTS = 6;

/** Slot experience the standalone route reads a placed preset from (getExperience by slug). */
export const SMILE_SCHEDULER_SLOT_SLUG = "smile/bb/scheduler";

/**
 * Studio definition id per service, the palette identity the presets register
 * and the node the route looks for in the slot experience. One per service,
 * checkup included since D15 (2026-09-08: it books with TAG's Other radio).
 */
export const SCHEDULER_PRESET_ID_FOR_SERVICE = {
  dentures: "bb-preset-threestep-dentures",
  emergency: "bb-preset-threestep-emergency",
  implants: "bb-preset-threestep-implants",
  checkup: "bb-preset-threestep-checkup",
  clearaligners: "bb-preset-threestep-aligners",
} as const satisfies Record<ThreeStepServiceKey, string>;

const pad = <T>(items: readonly T[], length: number, empty: () => T): T[] =>
  Array.from({ length }, (_, index) => items[index] ?? empty());

/** The code defaults: SHARED_COPY plus the service row; hidden-layer lines empty. */
export function schedulerContentDefaults(serviceKey: ThreeStepServiceKey | null): SchedulerContent {
  const service = serviceKey
    ? THREE_STEP_SERVICES.find((candidate) => candidate.key === serviceKey)
    : undefined;
  const confirmation = service?.confirmation;
  const shared = SHARED_COPY;
  return {
    headline: service?.headline ?? "",
    serviceName: service?.serviceName ?? "",
    availabilityLine: "",
    assistLinePrefix: "",
    helpLinePrefix: shared.needHelpPrefix,
    contactPhoneDisplay: shared.contactPhoneDisplay,
    contactPhoneNumber: shared.contactPhoneNumber,
    retry: shared.retry,
    requiredTag: shared.requiredTag,
    showSmsConsent: false,
    showGuardianFields: true,
    steps: { ...shared.steps },
    step1: { ...shared.step1, heading: "", subheading: "" },
    step2: {
      ...shared.step2,
      heading: "",
      subheading: "",
      knowHeading: "",
      knowSubheading: "",
      dobHelper: "",
      serviceQuestion: service?.serviceQuestion ?? "",
    },
    guarantor: { ...shared.guarantor },
    step3: { ...shared.step3, subheading: "" },
    fieldErrors: { ...PATIENT_FIELD_ERRORS },
    confirmation: {
      ...shared.confirmation,
      appointmentCardTitle: confirmation?.appointmentCardTitle ?? "",
      journeyHeading: confirmation?.journeyHeading ?? "",
      journeyVideoTitle: confirmation?.journeyVideoTitle ?? "",
      journeyVideoBody: confirmation?.journeyVideoBody ?? "",
      faqSubtitle: confirmation?.faqSubtitle ?? "",
      faqFootnoteMarker: confirmation?.faqFootnoteMarker ?? "",
      // Per-service photography wins where a board authors it; the shared
      // office photographs are the default for every service until then.
      heroImageSrc: confirmation?.heroImageSrc ?? shared.confirmation.heroImageSrc,
      heroImageAlt: confirmation?.heroImageAlt ?? shared.confirmation.heroImageAlt,
      journeyPosterSrc: confirmation?.journeyPosterSrc ?? "",
      journeyVideoSrc: confirmation?.journeyVideoSrc ?? "",
      faqImageSrc: confirmation?.faqImageSrc ?? shared.confirmation.faqImageSrc,
      faqImageAlt: confirmation?.faqImageAlt ?? shared.confirmation.faqImageAlt,
      faq: pad(
        (confirmation?.faq ?? []).map((item) => ({
          question: item.question,
          answer: item.answer,
          footnote: item.footnote ?? "",
        })),
        SCHEDULER_FAQ_SLOTS,
        () => ({ question: "", answer: "", footnote: "" }),
      ),
      carriers: pad(
        shared.insuranceCarriers.map((carrier) => ({ ...carrier })),
        SCHEDULER_CARRIER_SLOTS,
        () => ({ name: "", logoSrc: "" }),
      ),
    },
  };
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

function mergeInto<T>(base: T, override: unknown): T {
  if (override === undefined || override === null) return base;
  if (Array.isArray(base)) {
    if (!Array.isArray(override)) return base;
    // Slots are fixed by the defaults; an authored value beyond them has no home.
    return base.map((item, index) => mergeInto(item, override[index])) as T;
  }
  if (isPlainObject(base)) {
    if (!isPlainObject(override)) return base;
    const merged: Record<string, unknown> = { ...base };
    for (const key of Object.keys(override)) {
      if (!(key in base)) continue;
      merged[key] = mergeInto(base[key], override[key]);
    }
    return merged as T;
  }
  return typeof override === typeof base ? (override as T) : base;
}

/**
 * The seam: the service's code defaults deep-merged with whatever Contentful
 * authored. Unknown keys and type mismatches are ignored, so a stale or
 * malformed placement can never break the page. Never mutates the defaults.
 */
export function resolveSchedulerContent(
  serviceKey: ThreeStepServiceKey | null,
  authored?: SchedulerContentOverrides,
): SchedulerContent {
  const defaults = schedulerContentDefaults(serviceKey);
  return authored ? mergeInto(defaults, authored) : defaults;
}

/** Every leaf of the content tree as a dotted path ("confirmation.faq.1.question", 1-based slots). */
export function flattenSchedulerContent(
  content: SchedulerContent,
): Array<{ path: string; value: SchedulerContentLeaf }> {
  const leaves: Array<{ path: string; value: SchedulerContentLeaf }> = [];
  const walk = (node: unknown, path: string[]) => {
    if (typeof node === "string" || typeof node === "boolean") {
      leaves.push({ path: path.join("."), value: node });
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((item, index) => walk(item, [...path, String(index + 1)]));
      return;
    }
    if (isPlainObject(node)) {
      for (const key of Object.keys(node)) walk(node[key], [...path, key]);
    }
  };
  walk(content, []);
  return leaves;
}

const capitalise = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);
const camel = (segments: string[]) =>
  segments.map((segment, index) => (index === 0 ? segment : capitalise(segment))).join("");

/**
 * Deterministic Studio variable id for a content path (<= 32 chars, pinned by
 * content.test.ts). "headline" keeps the pre-existing headlineOverride id so
 * placements authored before the seam keep their value; the confirmation group
 * drops its prefix and the FAQ / carrier slots are numbered families.
 */
export function schedulerContentVariableId(path: string): string {
  if (path === "headline") return "headlineOverride";
  if (path === "serviceName") return "serviceDisplayName";
  const segments = path.split(".");
  if (segments[0] === "confirmation") {
    if (segments[1] === "faq") return `faq${segments[2]}${capitalise(segments[3])}`;
    if (segments[1] === "carriers") {
      return `carrier${segments[2]}${segments[3] === "logoSrc" ? "Src" : capitalise(segments[3])}`;
    }
    return camel(segments.slice(1));
  }
  if (segments[0] === "fieldErrors") return camel(["error", ...segments.slice(1)]);
  return camel(segments);
}

/**
 * The boards write the headline with a U+2028 line separator before
 * "3 Steps Away". The code default keeps the byte (headlineLines renders it as
 * a line break), but a Studio text field is one line, so the registered default
 * carries a plain space instead.
 */
export function normaliseHeadlineForStudio(text: string): string {
  return text.replace(/\s*[\u2028\n]\s*/g, " ");
}

/** The Studio-facing default for a leaf (the headline is the one leaf that differs). */
export function studioDefaultFor(path: string, value: SchedulerContentLeaf): SchedulerContentLeaf {
  return path === "headline" && typeof value === "string"
    ? normaliseHeadlineForStudio(value)
    : value;
}

function setAtPath(target: Record<string, unknown>, segments: string[], value: unknown): void {
  let cursor: Record<string, unknown> | unknown[] = target;
  segments.forEach((segment, index) => {
    const last = index === segments.length - 1;
    const nextIsSlot = !last && /^\d+$/.test(segments[index + 1]);
    const key: string | number = /^\d+$/.test(segment) ? Number(segment) - 1 : segment;
    if (last) {
      (cursor as Record<string | number, unknown>)[key] = value;
      return;
    }
    const existing = (cursor as Record<string | number, unknown>)[key];
    if (existing === undefined) {
      (cursor as Record<string | number, unknown>)[key] = nextIsSlot ? [] : {};
    }
    cursor = (cursor as Record<string | number, unknown>)[key] as
      | Record<string, unknown>
      | unknown[];
  });
}

/**
 * Inverse of the id mapping: the values a placed preset hands the component
 * (props keyed by variable id, or unbound values read off an experience) back
 * onto the content tree. Ids that are not content leaves (facilityCode,
 * analyticsId, isEditorMode, Studio's own props) and values of the wrong type
 * are ignored; undefined means "not authored".
 */
export function authoredFromVariables(
  values: Record<string, unknown>,
  serviceKey: ThreeStepServiceKey | null,
): SchedulerContentOverrides {
  const authored: Record<string, unknown> = {};
  for (const leaf of flattenSchedulerContent(schedulerContentDefaults(serviceKey))) {
    const value = values[schedulerContentVariableId(leaf.path)];
    if (value === undefined || value === null) continue;
    if (typeof leaf.value === "boolean") {
      if (typeof value === "boolean") setAtPath(authored, leaf.path.split("."), value);
      else if (value === "true" || value === "false") {
        setAtPath(authored, leaf.path.split("."), value === "true");
      }
      continue;
    }
    if (typeof value === "string") setAtPath(authored, leaf.path.split("."), value);
  }
  return authored as SchedulerContentOverrides;
}

interface ExperienceTreeNode {
  definitionId?: string;
  children?: ExperienceTreeNode[];
  variables?: Record<string, { type?: string; key?: string } | undefined>;
}

const findPresetNode = (nodes: unknown, presetId: string): ExperienceTreeNode | undefined => {
  if (!Array.isArray(nodes)) return undefined;
  for (const node of nodes) {
    if (!isPlainObject(node)) continue;
    const candidate = node as ExperienceTreeNode;
    if (candidate.definitionId === presetId) return candidate;
    const nested = findPresetNode(candidate.children, presetId);
    if (nested) return nested;
  }
  return undefined;
};

/**
 * Reads the manual (UnboundValue) variables of the first `presetId` node in a
 * fetched experience, keyed by variable id. Same access path the rest of the
 * renderer uses (entityStore.experienceEntryFields.componentTree +
 * entityStore.unboundValues, see studio-server-utils); typed unknown so a
 * failed fetch or a malformed tree authors nothing instead of throwing.
 * Entry-bound (BoundValue) variables resolve through the SDK at render time on
 * a Studio page; the standalone route reads manual values only.
 */
export function authoredVariablesFromExperience(
  experience: unknown,
  presetId: string,
): Record<string, SchedulerContentLeaf> {
  if (!isPlainObject(experience) || !isPlainObject(experience.entityStore)) return {};
  const store = experience.entityStore;
  const fields = isPlainObject(store.experienceEntryFields) ? store.experienceEntryFields : {};
  const tree = isPlainObject(fields.componentTree) ? fields.componentTree : {};
  const unbound = isPlainObject(store.unboundValues)
    ? store.unboundValues
    : isPlainObject(store._unboundValues)
      ? store._unboundValues
      : {};
  const node = findPresetNode(tree.children, presetId);
  if (!node?.variables) return {};
  const authored: Record<string, SchedulerContentLeaf> = {};
  for (const [id, variable] of Object.entries(node.variables)) {
    if (variable?.type !== "UnboundValue" || !variable.key) continue;
    const entry = unbound[variable.key];
    const value = isPlainObject(entry) ? entry.value : undefined;
    if (typeof value === "string" || typeof value === "boolean") authored[id] = value;
  }
  return authored;
}

/** Fills {token} placeholders; unknown tokens stay so a typo is visible, never blank. */
export function fillTemplate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{([a-zA-Z]+)\}/g, (token, name: string) =>
    name in values ? String(values[name]) : token,
  );
}

/**
 * Lines of a headline: the boards' U+2028 separator (or an authored newline)
 * is a line break the page renders as <br/>, never as a character (G-08).
 */
export function headlineLines(text: string): string[] {
  return text.split(/\u2028|\n/);
}

/**
 * Splits a marker-bearing string at its {footnote} placeholder. `after` is
 * null when the string carries no marker, so the caller renders it as is.
 */
export function splitFootnote(text: string): { before: string; after: string | null } {
  const token = "{footnote}";
  const index = text.indexOf(token);
  if (index === -1) return { before: text, after: null };
  return { before: text.slice(0, index), after: text.slice(index + token.length) };
}

const FIELD_ERROR_KEY_BY_MESSAGE = new Map(
  Object.entries(PATIENT_FIELD_ERRORS).map(([key, message]) => [message, key]),
) as Map<string, keyof typeof PATIENT_FIELD_ERRORS>;

/**
 * The shared validators (scheduling.helpers, part of the frozen widget) return
 * their own PATIENT_FIELD_ERRORS copy. Map a returned message onto the
 * authored field error for the same key; anything unknown passes through.
 */
export function translateFieldError(
  message: string,
  errors: SchedulerContent["fieldErrors"],
): string {
  const key = FIELD_ERROR_KEY_BY_MESSAGE.get(message);
  return key ? errors[key] : message;
}

/** tel: href for the authored contact number, whatever shape an author typed it in. */
export function telHrefFor(contactPhoneNumber: string): string {
  const digits = normalizePhoneDigits(contactPhoneNumber);
  return digits.length === 10 ? `tel:${toE164(digits)}` : `tel:${contactPhoneNumber}`;
}
